import React, { Suspense, lazy, useEffect, useState } from 'react'
import { useAppDispatch } from '../../../store'
import { type Row, type ApplyFiltersAndDispatchParams } from '../../../components/CustomTable/types'
import PAGE_TITLES from '../../../consts/titles'
import LoadingPageV2 from '../../../pages/LoadingPageV2'
import { useParams } from 'react-router-dom'
import { getEnrollmentList, enrollmentsLoading } from '../../../store/slices/termsSectionsSlice/selectors'
import { DeleteEnrollment } from './DeleteEnrollment'
import { Box, Typography } from '@mui/material'
import { TopbarActions } from './TopbarActions'
import { getEnrollmentsApi } from '../../../store/slices/termsSectionsSlice/api'
import { exportCsv } from '../../../utils/exportCsv'
import { notification, closeNotification } from '../../../components/Notifications'
import { getTerm } from '../../../store/slices/termsSlice/selectors'
import { TermsManager } from '../../../APIManager'
import { useMutation } from '@tanstack/react-query'
import CreateEnrollment from './CreateEnrollment'
import { cdgCellCommonStyles } from '../../../components/CustomTable/generateCells'
import { black, blue } from '../../../colors'
import GenericModal from '../../../components/GenericModal'
import { useAppSearchParams } from '../../../hooks/useAppSearchParams'
import ExportProcessingModal from '../../../components/ExportProcessingModal'
import { useTaskStatus } from '../../../hooks/useTaskStatus'
import { ExportBackgroundTaskRunningModal } from '../../../components/ExportBackgroundTaskRunningModal'

interface Instructor {
  userId: string
  name: string
  email: string
  isInstructorSelected?: boolean
}

const AppTable = lazy(async () => await import('../../../components/CustomTable'))

const Enrollments: React.FC = () => {
  const { searchParams } = useAppSearchParams()
  const selectedScope = searchParams.get('scope') as 'my' | 'all'
  const { id: urlTermId } = useParams()
  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>({ pageSize: 10, scope: selectedScope ?? 'my' })
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] = useState<any>()
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [exportModalMessage, setExportModalMessage] = useState('')
  const loading = enrollmentsLoading()
  const enrollmentsList = getEnrollmentList()
  const dispatch = useAppDispatch()
  const term = getTerm()
  const headerName = enrollmentsList?.header?.headerName || 'Enrollments'

  const {
    taskId: uploadTaskId,
    setTaskId: setUploadTaskId,
    showBackgroundModal: showUploadBlockedModal,
    setShowBackgroundModal: setShowUploadBlockedModal,
    isCheckingStatus: isUploadRunning
  } = useTaskStatus('uploadTask_enrollments')

  const {
    taskId: exportTaskId,
    setTaskId: setExportTaskId,
    showBackgroundModal: showExportBlockedModal,
    setShowBackgroundModal: setShowExportBlockedModal,
    isCheckingStatus: isExportRunning,
    checkStatusBeforeExport
  } = useTaskStatus('exportTask_enrollments')

  const isAnyTaskRunning = isUploadRunning || isExportRunning

  const openBlockedModal = (): void => {
    if (isUploadRunning) setShowUploadBlockedModal(true)
    else setShowExportBlockedModal(true)
  }

  useEffect(() => {
    if (urlTermId && params && Object.keys(params).length > 0) {
      void dispatch(getEnrollmentsApi({ urlId: urlTermId, params }))
    }
  }, [JSON.stringify(params), urlTermId])

  const { mutate: exportEnrollmentsCSV, isPending: exportCSVLoading } = useMutation({
    mutationFn: async ({ termId, params }: { termId: string, params: ApplyFiltersAndDispatchParams }) => await TermsManager.exportEnrollmentsCSV(termId ?? '', params),
    onMutate: () => notification('Preparing download...', { variant: 'loading', persist: true }),
    onSuccess: (response, _variables, context) => {
      if (response?.status === 202) {
        if (response?.data?.taskId) {
          setExportTaskId(response.data.taskId)
        }
        closeNotification(context)
        setExportModalMessage(response?.data?.message || '')
        setExportModalOpen(true)
        return
      }
      const data = response?.data
      if (data) {
        exportCsv({ data, headers: response?.headers, loadingToastKey: context })
      } else {
        closeNotification(context)
      }
    },
    onError: (error: any, _variables, context) => {
      closeNotification(context)
      notification(error?.message ?? 'Export csv failed', { variant: 'error' })
    },
    meta: { silent: true }
  })

  const handleExport = async (): Promise<void> => {
    if (isUploadRunning) { openBlockedModal(); return }
    const canExport = await checkStatusBeforeExport()
    if (!canExport) return
    exportEnrollmentsCSV({ termId: urlTermId ?? '', params })
  }

  /**
   * Overrides the rendering of the "instructors" column in a table row.
   * - If there are no instructors, it renders an empty Typography component.
   * - If the enrollment is for all instructors, it displays a placeholder ("--").
   * - Otherwise, it shows the first selected instructor's name. If there are more selected instructors,
   *   it displays a clickable "+N" (where N is the number of additional instructors), which opens a modal
   *   to show the full list of selected instructors.
  **/
  const overrides = {
    instructors: (cell: Record<string, any>) => {
      const { instructors = [], isEnrollmentForAllInstructors } = cell

      if (!instructors.length) return <Typography variant="body2" />

      const selectedInstructors = instructors.filter(
        (inst: Instructor) => !!inst?.isInstructorSelected
      )

      const first = selectedInstructors[0]
      const remaining = Math.max(selectedInstructors.length - 1, 0)
      const isMoreThanOne = remaining > 0

      const openModal = (): void => {
        setShowUpdateModal(true)
      }

      const isClickable = isMoreThanOne

      return (
        <Typography
          variant="body2"
          sx={{
            ...cdgCellCommonStyles,
            cursor: isClickable ? 'pointer' : 'default'
          }}
          onClick={isClickable ? openModal : undefined}
        >
          {first?.name ?? ''}
          {isMoreThanOne && (
            <>
              {', '}
              <Typography
                component="span"
                sx={{ color: blue[500], textDecoration: 'underline' }}
              >
                +{remaining}
              </Typography>
            </>
          )}
        </Typography>
      )
    }
  }

  const allowedActions = enrollmentsList?.actions ?? []
  return (
    <>
      <Box>
        <Suspense fallback={<LoadingPageV2 title={PAGE_TITLES.TERMS} />}>
          <AppTable
            createAction={<CreateEnrollment allowedActions={allowedActions} params={params} isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
            exportButtonProps={{ disabled: exportCSVLoading || isAnyTaskRunning, loading: exportCSVLoading || isAnyTaskRunning }}
            customTopbarAction={
              <TopbarActions
                handleExport={() => { void handleExport() }}
                params={params}
                termId={urlTermId ?? ''}
                allowedActions={allowedActions}
                setUploadTaskId={setUploadTaskId}
                isTaskRunning={isAnyTaskRunning}
                onTaskBlock={openBlockedModal}
              />
            }
            editAction={<CreateEnrollment allowedActions={allowedActions} params={params} isEditAction={true} isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
            list={enrollmentsList}
            loading={loading}
            deleteAction={<DeleteEnrollment params={params} isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
            featureName="Enrollment"
            params={params}
            setParams={setParams}
            overrides={overrides}
            showScopeToggle={true}
            scopeToggleTitle={headerName}
            onRowClick={(row: Record<string, any>) => { setSelectedRow(row?.row?.data) }}
          />
        </Suspense>
      </Box>

      {
        showUpdateModal &&
        <GenericModal
          isOpen={showUpdateModal}
          title={'Edit Enrollment'}
          onClose={() => { setShowUpdateModal?.(false) }}
          maxWidth='sm'
          action={false}
        >
          <CreateEnrollment allowedActions={allowedActions} isEditAction={true} selectedRow={selectedRow} params={params} handleCloseModal={() => { setShowUpdateModal(false) }} />
        </GenericModal>
      }

      <ExportProcessingModal
        isOpen={exportModalOpen}
        onClose={() => { setExportModalOpen(false) }}
        message={exportModalMessage}
      />

      <ExportBackgroundTaskRunningModal
        isOpen={showExportBlockedModal}
        onClose={() => { setShowExportBlockedModal(false) }}
        taskId={exportTaskId ?? ''}
      />
      <ExportBackgroundTaskRunningModal
        isOpen={showUploadBlockedModal}
        onClose={() => { setShowUploadBlockedModal(false) }}
        taskId={uploadTaskId ?? ''}
      />
    </>
  )
}

export default Enrollments
